/**
 * Kleda Client App - UpsellCard Component
 * Herbruikbare, conversie-geoptimaliseerde upsell kaart
 *
 * Gebruikt voor alle drie upsells: ondertapijt, rand reparatie, vlekkenbaas
 * Stripe-level design: clean, focused, conversion-optimized
 */

import { useState, useRef, useEffect } from 'react'
import { Icon } from './UI'
import { trackUpsellViewed, trackUpsellConfirmed, trackUpsellDeclined } from '../analytics'

// =============================================================================
// TYPES
// =============================================================================

export type UpsellVariant = 'ondertapijt' | 'rand-reparatie' | 'vlekkenbaas'
export type UpsellStatus = 'available' | 'accepted' | 'declined'

interface UpsellCardProps {
  variant: UpsellVariant
  status: UpsellStatus
  werkOrderId: string
  headline: string
  description: string
  price: string
  priceSubtext?: string           // e.g. "2x € 17,50" when quantity > 1
  originalPrice?: string
  benefits: string[]
  ctaLabel: string
  badge?: string
  imageUrl?: string
  highlighted?: boolean
  isSubmitting?: boolean
  error?: string | null
  compact?: boolean
  onAccept: () => void
  onDecline?: () => void
  // Quantity selector
  quantity?: number
  onQuantityChange?: (q: number) => void
  maxQuantity?: number
  // Custom accepted message
  acceptedMessage?: string
}

// =============================================================================
// VARIANT ICONS
// =============================================================================

function VariantIcon({ variant }: { variant: UpsellVariant }) {
  switch (variant) {
    case 'rand-reparatie':
      return (
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-100">
          <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17l-4.655-5.163a2.18 2.18 0 01-.14-2.838l.577-.808A2.18 2.18 0 019.37 5.5h5.26a2.18 2.18 0 012.168.861l.577.808a2.18 2.18 0 01-.14 2.838L12.58 15.17a.836.836 0 01-1.16 0z" />
          </svg>
        </div>
      )
    case 'ondertapijt':
      return (
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
          <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0L12 16.5l-5.571-2.25m11.142 0L21.75 16.5 12 21.75 2.25 16.5l4.179-2.25" />
          </svg>
        </div>
      )
    case 'vlekkenbaas':
      return (
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-secondary/10">
          <svg className="w-6 h-6 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
          </svg>
        </div>
      )
  }
}

// =============================================================================
// UPSELL CARD COMPONENT
// =============================================================================

export function UpsellCard({
  variant,
  status,
  werkOrderId,
  headline,
  description,
  price,
  priceSubtext,
  originalPrice,
  benefits,
  ctaLabel,
  badge,
  imageUrl,
  highlighted = false,
  isSubmitting = false,
  error,
  compact = false,
  onAccept,
  onDecline,
  quantity,
  onQuantityChange,
  maxQuantity = 5,
  acceptedMessage,
}: UpsellCardProps) {
  const [isDeclining, setIsDeclining] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const hasTrackedView = useRef(false)

  // Track view once
  useEffect(() => {
    if (!hasTrackedView.current && status === 'available') {
      const upsellType = variant === 'rand-reparatie' ? 'rand_reparatie' : variant
      trackUpsellViewed(upsellType as 'ondertapijt' | 'rand_reparatie' | 'vlekkenbaas', werkOrderId, parseFloat(price.replace(/[^0-9,.]/g, '').replace(',', '.')))
      hasTrackedView.current = true
    }
  }, [status, variant, werkOrderId, price])

  // Scroll into view if highlighted
  useEffect(() => {
    if (highlighted && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [highlighted])

  const handleAccept = () => {
    const upsellType = variant === 'rand-reparatie' ? 'rand_reparatie' : variant
    trackUpsellConfirmed(upsellType as 'ondertapijt' | 'rand_reparatie' | 'vlekkenbaas', werkOrderId, parseFloat(price.replace(/[^0-9,.]/g, '').replace(',', '.')))
    onAccept()
  }

  const handleDecline = () => {
    if (onDecline) {
      const upsellType = variant === 'rand-reparatie' ? 'rand_reparatie' : variant
      trackUpsellDeclined(upsellType as 'ondertapijt' | 'rand_reparatie' | 'vlekkenbaas', werkOrderId)
      setIsDeclining(true)
      onDecline()
    }
  }

  // Accepted state
  if (status === 'accepted') {
    return (
      <div
        ref={cardRef}
        className={`card border-2 border-green-300 bg-green-50 ${compact ? '' : ''}`}
      >
        <div className="flex flex-col items-center text-center py-sm">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-sm">
            <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
          <p className="text-body font-semibold text-green-800">
            {acceptedMessage || `${headline.replace('?', '')} toegevoegd!`}
          </p>
          <p className="text-body-sm text-green-700 mt-xs">
            Wordt meegeleverd bij je aflevering
          </p>
        </div>
      </div>
    )
  }

  // Declined state
  if (status === 'declined' || isDeclining) {
    return null
  }

  // Compact variant — for sidebar
  if (compact) {
    return (
      <div
        ref={cardRef}
        className={`
          card transition-all
          ${highlighted ? 'ring-2 ring-primary/20' : ''}
        `}
      >
        {imageUrl && (
          <div className="-mx-lg -mt-lg mb-sm rounded-t-premium overflow-hidden">
            <img
              src={imageUrl}
              alt={headline}
              className="w-full h-24 object-contain bg-white"
            />
          </div>
        )}

        <div className="flex items-start justify-between gap-sm mb-sm">
          <h3 className="text-body font-semibold text-ink">{headline}</h3>
          <span className="text-body font-semibold text-primary whitespace-nowrap">{price}</span>
        </div>

        <p className="text-body-sm text-ink-muted mb-sm">
          {description}
        </p>

        {/* Compact quantity selector */}
        {onQuantityChange && quantity != null && (
          <div className="flex items-center gap-sm mb-sm">
            <span className="text-body-xs text-ink-muted">Aantal:</span>
            <div className="flex items-center border border-border-subtle rounded-kleda-md">
              <button
                type="button"
                onClick={() => onQuantityChange(quantity - 1)}
                disabled={quantity <= 1}
                className="w-8 h-8 flex items-center justify-center text-sm font-medium text-ink-soft hover:bg-surface-soft disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-l-kleda-md"
                aria-label="Eén minder"
              >
                &minus;
              </button>
              <span className="w-8 h-8 flex items-center justify-center text-body-sm font-semibold text-ink border-x border-border-subtle">
                {quantity}
              </span>
              <button
                type="button"
                onClick={() => onQuantityChange(quantity + 1)}
                disabled={quantity >= maxQuantity}
                className="w-8 h-8 flex items-center justify-center text-sm font-medium text-ink-soft hover:bg-surface-soft disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-r-kleda-md"
                aria-label="Eén meer"
              >
                +
              </button>
            </div>
            {priceSubtext && (
              <span className="text-body-xs text-ink-muted">{priceSubtext}</span>
            )}
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-700 px-sm py-xs rounded-kleda-md mb-sm text-body-xs">
            {error}
          </div>
        )}

        <button
          onClick={handleAccept}
          disabled={isSubmitting}
          className="btn-primary w-full text-body-sm py-xs"
        >
          {isSubmitting ? 'Bezig...' : ctaLabel}
        </button>

      </div>
    )
  }

  // Full variant — for inline use
  return (
    <div
      ref={cardRef}
      className={`
        card transition-all
        ${highlighted ? 'ring-2 ring-primary/20' : ''}
      `}
    >
      {/* Product image */}
      {imageUrl && (
        <div className="-mx-lg -mt-lg mb-md rounded-t-premium overflow-hidden">
          <img
            src={imageUrl}
            alt={headline}
            className="w-full h-44 sm:h-52 object-contain bg-white"
          />
        </div>
      )}

      {/* Badge — als stille chip */}
      {badge && (
        <div className="mb-md">
          <span className="inline-block px-sm py-[3px] bg-chip-amber border border-chip-amber-line text-chip-amber-ink text-[12px] font-bold rounded-full">
            {badge}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-md mb-md">
        <VariantIcon variant={variant} />
        <div className="flex-1 min-w-0">
          <h3 className="text-heading-sm font-bold text-ink mb-xs">
            {headline}
          </h3>
          <p className="text-body text-ink-soft">
            {description}
          </p>
        </div>
      </div>

      {/* Price */}
      <div className="flex items-baseline gap-sm mb-md">
        {originalPrice && (
          <span className="text-body text-ink-muted line-through">{originalPrice}</span>
        )}
        <span className="text-heading-sm font-bold text-primary">{price}</span>
        {priceSubtext && (
          <span className="text-body-sm text-ink-muted">{priceSubtext}</span>
        )}
      </div>

      {/* Quantity selector */}
      {onQuantityChange && quantity != null && (
        <div className="flex items-center gap-md mb-md">
          <span className="text-body-sm text-ink-soft">Aantal:</span>
          <div className="flex items-center border border-border-subtle rounded-kleda-md">
            <button
              type="button"
              onClick={() => onQuantityChange(quantity - 1)}
              disabled={quantity <= 1}
              className="w-10 h-10 flex items-center justify-center text-lg font-medium text-ink-soft hover:bg-surface-soft disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-l-kleda-md"
              aria-label="Eén minder"
            >
              &minus;
            </button>
            <span className="w-10 h-10 flex items-center justify-center text-body font-semibold text-ink border-x border-border-subtle">
              {quantity}
            </span>
            <button
              type="button"
              onClick={() => onQuantityChange(quantity + 1)}
              disabled={quantity >= maxQuantity}
              className="w-10 h-10 flex items-center justify-center text-lg font-medium text-ink-soft hover:bg-surface-soft disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-r-kleda-md"
              aria-label="Eén meer"
            >
              +
            </button>
          </div>
        </div>
      )}

      {/* Benefits */}
      {benefits.length > 0 && (
        <div className="bg-surface-soft rounded-kleda-md p-md mb-md">
          <div className="space-y-xs text-body-sm">
            {benefits.map((benefit, i) => (
              <div key={i} className="flex items-center gap-xs text-ink-soft">
                <span className="text-primary font-semibold flex-shrink-0">&#10003;</span>
                {benefit}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-md py-sm rounded-kleda-md mb-md text-body-sm flex items-start gap-sm">
          <Icon name="alert" className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* CTA */}
      <button
        onClick={handleAccept}
        disabled={isSubmitting}
        className="btn-primary w-full mb-sm"
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-sm">
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Bezig...
          </span>
        ) : (
          ctaLabel
        )}
      </button>

      {/* Secondary action */}
      <div className="flex items-center justify-end">
        {onDecline && (
          <button
            onClick={handleDecline}
            className="text-body-sm text-ink-muted hover:text-ink-soft transition-colors"
          >
            Nee, bedankt
          </button>
        )}
      </div>
    </div>
  )
}
